import React from 'react';

interface BarChartProps {
  data: Record<string, any>[];
  keys?: string[];
  indexBy?: string;
  colors?: string[];
  currency?: string | null;
}

export const BarChart: React.FC<BarChartProps> = ({ data, keys, indexBy, colors, currency }) => {
    // Multi-series grouped bar chart logic with negative value support
    if (keys && indexBy && colors) {
        const colorMap = keys.reduce((acc, key, index) => {
            acc[key] = colors[index % colors.length];
            return acc;
        }, {} as Record<string, string>);

        const allValues = data.flatMap(d => keys.map(key => d[key] || 0));
        const maxValue = Math.max(0, ...allValues);
        const minValue = Math.min(0, ...allValues);
        const range = maxValue - minValue;

        if (data.length === 0 || keys.length === 0 || range === 0) {
            return <div className="h-[300px] flex items-center justify-center text-center text-gray-500">Vyberte alespoň jeden rok pro zobrazení dat.</div>;
        }

        const svgHeight = 300;
        const topPadding = 20;
        const bottomPadding = 30; // For labels
        const chartAreaHeight = svgHeight - topPadding - bottomPadding; // 250

        const zeroLineY = (maxValue / range) * chartAreaHeight;

        const groupPadding = 30;
        const barPadding = 2;
        const groupWidth = 80;
        const barWidth = (groupWidth - (keys.length - 1) * barPadding) / keys.length;
        const chartWidth = data.length * (groupWidth + groupPadding);
        
        return (
            <div className="w-full">
                <div className="w-full h-[300px] overflow-x-auto">
                    <svg width={chartWidth} height={svgHeight} className="font-sans">
                        <g transform={`translate(0, ${topPadding})`}>
                             {/* Zero line */}
                            <line x1="0" y1={zeroLineY} x2={chartWidth} y2={zeroLineY} stroke="#9ca3af" strokeWidth="1" />
                            
                            {data.map((d, i) => {
                                const groupX = i * (groupWidth + groupPadding);
                                return (
                                    <g key={d[indexBy]} transform={`translate(${groupX}, 0)`}>
                                        {keys.map((key, j) => {
                                            const value = d[key] || 0;
                                            const barHeight = (Math.abs(value) / range) * chartAreaHeight;
                                            const x = j * (barWidth + barPadding);
                                            const y = value >= 0 ? zeroLineY - barHeight : zeroLineY;
                                            
                                            return (
                                                <g key={key}>
                                                    <rect
                                                        x={x}
                                                        y={y}
                                                        width={barWidth}
                                                        height={barHeight}
                                                        fill={colorMap[key]}
                                                        fillOpacity={value >= 0 ? 1 : 0.6}
                                                        className="transition-all"
                                                    >
                                                      <title>{`${d[indexBy]} ${key}: ${value.toLocaleString('cs-CZ', {style: 'currency', currency: currency || 'CZK'})}`}</title>
                                                    </rect>
                                                </g>
                                            );
                                        })}
                                        <text
                                            x={groupWidth / 2}
                                            y={chartAreaHeight + 15}
                                            textAnchor="middle"
                                            fontSize="12"
                                            fill="#374151"
                                        >
                                            {d[indexBy]}
                                        </text>
                                    </g>
                                );
                            })}
                        </g>
                    </svg>
                </div>
                <div className="flex justify-center items-center flex-wrap gap-4 mt-4">
                    {keys.map(key => (
                        <div key={key} className="flex items-center gap-2 text-sm">
                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: colorMap[key] }}></span>
                            <span>{key}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // --- Single-series bar chart logic (for PriceTracker) ---
    const maxValue = Math.max(...data.map(d => d.value || 0), 0);
    const chartHeight = 200;
    const barWidth = 20;
    const barMargin = 15;
    const chartWidth = data.length * (barWidth + barMargin);

    if (data.length === 0) return <p className="text-center text-gray-500">Žádná data k zobrazení.</p>;

    return (
        <div className="w-full h-[250px] overflow-x-auto">
            <svg width={chartWidth} height={250} className="font-sans">
                <g transform="translate(0, 20)">
                    {data.map((d, i) => {
                        const value = d.value || 0;
                        const barHeight = maxValue > 0 ? (value / maxValue) * chartHeight : 0;
                        const x = i * (barWidth + barMargin);
                        const y = chartHeight - barHeight;
                        return (
                            <g key={d.name}>
                                <rect
                                    x={x}
                                    y={y}
                                    width={barWidth}
                                    height={barHeight}
                                    fill="#3B82F6"
                                    className="transition-all"
                                />
                                <text
                                    x={x + barWidth / 2}
                                    y={y - 5}
                                    textAnchor="middle"
                                    fontSize="12"
                                    fill="#6B7280"
                                >
                                    {value.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })}
                                </text>
                                <text
                                    x={x + barWidth / 2}
                                    y={chartHeight + 15}
                                    textAnchor="middle"
                                    fontSize="12"
                                    fill="#374151"
                                >
                                    {d.name}
                                </text>
                            </g>
                        );
                    })}
                </g>
            </svg>
        </div>
    );
};